/**
 * VoiceNotes: record a voice note in a comment box (Feedback, point and comment; #717). Record, stop, play it back,
 * delete it, record another. Each note is sent as soon as it stops: the server keeps the audio with the report, writes
 * it out and suggests a short summary, which goes into the comment box (`onSummary`) for the person to edit. The full
 * transcript stays with the note, folded. A note is at most three minutes; recording stops itself there.
 */
import { useEffect, useRef, useState } from "react";
import type { ApiError } from "../../api/client";
import { deleteVoiceNote, uploadVoiceNote } from "../../api/feedback";
import { Button } from "../kit";

/** The server's limit (FEEDBACK_VOICE_MAX_SECONDS) and how many notes a report takes. */
export const MAX_SECONDS = 180;
export const MAX_NOTES = 5;
const TOO_LARGE = "This voice note is too large to send. Record a shorter one.";
/** What MediaRecorder can make, best first: webm/opus (Chromium, Firefox), mp4 (Safari). */
const TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];

interface Note {
  key: number;
  url: string;
  seconds: number;
  state: "sending" | "sent" | "unsent";
  id?: number;
  transcript?: string;
  message?: string;
}

export interface VoiceNotesState {
  /** The sent notes' ids, for the report. */
  ids: number[];
  /** True while a note records or is on its way: the report waits. */
  busy: boolean;
}

/** "0:42". */
const fmtLength = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/** The line a summary goes into the comment box under. */
const summaryLabel = (index: number): string => (index === 1 ? "Summary of your voice note:" : `Summary of voice note ${index}:`);

const canRecord = () => typeof window !== "undefined" && typeof window.MediaRecorder === "function" && !!navigator.mediaDevices?.getUserMedia;

export function VoiceNotes({ onSummary, onChange }: { onSummary: (text: string) => void; onChange: (state: VoiceNotesState) => void }) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  // Said once to a screen reader when a recording starts and when it stops; the clock itself is not read out.
  const [said, setSaid] = useState("");
  // While the microphone is being asked for, Record is disabled: a second press never asks twice.
  const [starting, setStarting] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const began = useRef(0);
  // The notes as they are now, for an answer that lands before React has drawn the note it answers (a fast server).
  const shown = useRef<Note[]>([]);
  const change = (next: (all: Note[]) => Note[]) => {
    shown.current = next(shown.current);
    setNotes(shown.current);
  };
  const seq = useRef(0);
  const live = useRef(true);
  const latest = useRef({ onSummary, onChange });
  useEffect(() => {
    latest.current = { onSummary, onChange };
  });

  useEffect(() => {
    latest.current.onChange({ ids: notes.flatMap((n) => (n.id ? [n.id] : [])), busy: recording || notes.some((n) => n.state === "sending") });
  }, [notes, recording]);

  // Closing the box stops a recording and lets go of the microphone and the recordings.
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      recorder.current?.stream.getTracks().forEach((track) => track.stop());
      if (recorder.current?.state === "recording") recorder.current.stop();
      latest.current.onChange({ ids: [], busy: false });
    };
  }, []);
  const urls = useRef<string[]>([]);
  useEffect(() => () => urls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const send = (key: number, audio: Blob, seconds: number) => {
    uploadVoiceNote(audio, seconds)
      .then((res) => {
        const index = shown.current.findIndex((n) => n.key === key) + 1;
        // Deleted (or the box closed) while it was on its way: it goes on the server too.
        if (!live.current || index === 0) return void deleteVoiceNote(res.id).catch(() => undefined);
        change((all) => all.map((n) => (n.key === key ? { ...n, state: "sent", id: res.id, seconds: res.duration || n.seconds, transcript: res.transcript, message: res.message } : n)));
        if (res.summary) latest.current.onSummary(`${summaryLabel(index)}\n${res.summary}`);
      })
      .catch((error: ApiError) => {
        // Too big for the server (its own words) or for the proxy in front of it (413, no words); else a plain retry.
        const message = error.status === 413 ? TOO_LARGE : error.status === 400 && error.detail ? error.detail : "Not sent. Delete it and record again.";
        if (live.current) change((all) => all.map((n) => (n.key === key ? { ...n, state: "unsent", message } : n)));
      });
  };

  const start = async () => {
    setStarting(true);
    setProblem(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setProblem("The microphone is not allowed. Allow it for this site in the browser, then try again.");
      return;
    } finally {
      if (live.current) setStarting(false);
    }
    if (!live.current) return stream.getTracks().forEach((track) => track.stop());
    const mimeType = TYPES.find((type) => MediaRecorder.isTypeSupported?.(type));
    // 64 kbps: speech stays clear, and three minutes stays well under the 5 MB the server takes.
    const rec = new MediaRecorder(stream, { ...(mimeType && { mimeType }), audioBitsPerSecond: 64_000 });
    const chunks: Blob[] = [];
    rec.ondataavailable = (event) => event.data.size > 0 && chunks.push(event.data);
    rec.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      recorder.current = null;
      if (!live.current) return;
      setRecording(false);
      setSaid("Recording stopped");
      const seconds = Math.min(MAX_SECONDS, (Date.now() - began.current) / 1000);
      const audio = new Blob(chunks, { type: rec.mimeType || mimeType || "audio/webm" });
      const url = URL.createObjectURL(audio);
      urls.current.push(url);
      const key = (seq.current += 1);
      change((all) => [...all, { key, url, seconds, state: "sending" }]);
      send(key, audio, seconds);
    };
    recorder.current = rec;
    rec.start();
    began.current = Date.now();
    setElapsed(0);
    setRecording(true);
    setSaid("Recording");
  };

  // The clock while recording; at the limit the recording stops itself.
  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => {
      const now = (Date.now() - began.current) / 1000;
      if (now >= MAX_SECONDS) recorder.current?.stop();
      else setElapsed(now);
    }, 250);
    return () => window.clearInterval(timer);
  }, [recording]);

  const remove = (note: Note) => {
    change((all) => all.filter((n) => n.key !== note.key));
    if (note.id) void deleteVoiceNote(note.id).catch(() => undefined);
  };

  if (!canRecord() && notes.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {notes.map((note, i) => (
        <div key={note.key} className="flex flex-col gap-2 rounded-lg border-hair border-border bg-card p-2.5" aria-label={`Voice note ${i + 1}`} role="group">
          <div className="flex items-center justify-between gap-2">
            <span className="text-meta font-medium text-foreground">
              Voice note {i + 1} <span className="tabular font-normal text-muted-foreground">· {fmtLength(note.seconds)}</span>
            </span>
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(note)} aria-label={`Delete voice note ${i + 1}`}>
              Delete
            </Button>
          </div>
          {/* A recording of the person's own voice: there is nothing to caption. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls src={note.url} className="h-8 w-full" />
          {note.state === "sending" && (
            <p role="status" className="text-meta text-muted-foreground">
              Writing it out…
            </p>
          )}
          {note.message && <p className="text-meta text-secondary-foreground">{note.message}</p>}
          {note.transcript && (
            <details className="text-meta text-secondary-foreground">
              <summary className="cursor-pointer select-none text-muted-foreground">Transcript</summary>
              <p className="mt-1 whitespace-pre-wrap">{note.transcript}</p>
            </details>
          )}
        </div>
      ))}
      {recording ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border-hair border-border bg-card p-1.5 pl-3">
          <span className="flex items-center gap-2 text-meta text-foreground">
            <span aria-hidden className="size-2 animate-pulse rounded-full bg-destructive" />
            Recording <span className="tabular text-muted-foreground">{fmtLength(elapsed)} of {fmtLength(MAX_SECONDS)}</span>
          </span>
          <Button type="button" variant="surface" size="sm" onClick={() => recorder.current?.stop()}>
            Stop
          </Button>
        </div>
      ) : (
        notes.length < MAX_NOTES &&
        canRecord() && (
          <Button type="button" variant="surface" size="sm" className="self-start" disabled={starting} onClick={() => void start()}>
            {notes.length ? "Record another" : "Record a voice note"}
          </Button>
        )
      )}
      <p role="status" className="sr-only">
        {said}
      </p>
      {problem && (
        <p role="alert" className="text-meta text-destructive">
          {problem}
        </p>
      )}
    </div>
  );
}
